/** Full INR display — never compact to L/Cr. Shrink text in UI when needed. */
export function formatInrFull(n: number): string {
  return `₹${Math.round(Math.max(0, Number(n) || 0)).toLocaleString('en-IN')}`;
}

/** Rupees with paise when there are any: ₹2,986.30, ₹5,000. */
export function formatInrPaise(n: number): string {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  const hasPaise = Math.abs(v % 1) > 0.0001;
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: hasPaise ? 2 : 0, maximumFractionDigits: 2 })}`;
}

/** Format a numeric string with Indian-style commas while typing (digits only). */
export function formatAmountInput(raw: string): string {
  const cleaned = String(raw ?? '').replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const [intPart, decPart] = cleaned.split('.');
  const digits = intPart.replace(/^0+(?=\d)/, '') || (cleaned.startsWith('0') ? '0' : '');
  const withCommas = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (decPart != null) return `${withCommas}.${decPart.slice(0, 2)}`;
  return withCommas;
}

/** Parse comma-formatted amount input back to a number. */
export function parseAmountInput(raw: string): number {
  const n = parseFloat(String(raw ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigits(n: number): string {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return `${TENS[t]}${o ? ` ${ONES[o]}` : ''}`.trim();
}

function threeDigits(n: number): string {
  if (n < 100) return twoDigits(n);
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return `${ONES[h]} Hundred${rest ? ` ${twoDigits(rest)}` : ''}`.trim();
}

/** Indian numbering words for the typed amount (e.g. "One Lakh Thirteen Thousand Two Hundred"). */
export function amountToEnglishWords(n: number): string {
  const amount = Math.round(Math.max(0, Number(n) || 0));
  if (amount === 0) return 'Zero Rupees';
  if (amount > 999_99_99_999) return 'Amount too large';

  const crore = Math.floor(amount / 1_00_00_000);
  const lakh = Math.floor((amount % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((amount % 1_00_000) / 1_000);
  const hundred = amount % 1_000;

  const parts: string[] = [];
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (hundred) parts.push(threeDigits(hundred));

  return `${parts.join(' ')} Rupees Only`;
}
